import React, {useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import * as Yup from "yup";
import {FormikProps, useFormik} from "formik";
import {useIntl} from "react-intl";

import {Auth, CheckBox, FormInput, notify} from "@housebets/housebets-x-package";

import {ISignUpProps, TFormikConfig} from "./types";
import {useAppSelector} from "hooks/useAppSelector";
import apiAuth from "app/api/auth";
import {setLoginType} from "app/store/slices/setting";
import {LoginType} from "app/types/common";
import {useGTMEvents} from "app/hooks/useGTMEvents";
import {AuthLinks} from "../AuthLinks";
import {useDispatch} from "react-redux";
import {useLangNavigate} from "app/hooks/useLangNavigate";
import {getEmailSchema, getPasswordSchema} from "app/utils/schemaValidation";

const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
    formik.setValues({
        name: "",
        email: "",
        password: "",
        referral: "",
        confirm: false,
    });
    formik.setErrors({});
    formik.setTouched({});
    setError?.(false);
};

export const SignUp: React.FC<ISignUpProps> = ({onSubmit, onTabChange}) => {
    const {$t} = useIntl();
    const navigate = useNavigate();
    const {form} = useGTMEvents();
    const langNavigate = useLangNavigate();

    const formSchema = () =>
        Yup.object().shape({
            name: Yup.string().required($t({id: "FieldIsRequired"})),
            email: getEmailSchema($t),
            password: getPasswordSchema($t),
            confirm: Yup.boolean()
                .required($t({id: "FieldIsRequired"}))
                .oneOf([true], "Error"),
        });

    const [isReferral, setIsReferral] = useState(false);
    const [, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const inviteCode = useAppSelector((state) => state.setting.inviteCode);
    const promoCode = useAppSelector((state) => state.setting.promoCode);
    const dispatch = useDispatch();

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {
            name: "",
            email: "",
            password: "",
            referral: "",
            confirm: false,
        },
        validationSchema: formSchema(),
        validateOnBlur: true,
        onSubmit: async (data) => {
            try {
                setIsLoading(true);
                const res = await apiAuth.checkUserExistByAccount(data.email, "email");
                if (res.status === 200 && res.data.code === 1) {
                    initForm(formik);
                    onSubmit(data);
                    form.event({
                        event: "form_submit",
                        form_id: "signup_form",
                        form_name: "Sign Up Form",
                    });
                } else {
                    notify({
                        type: "error",
                        title: $t({id: "REGISTER_ERROR_6"}),
                    });
                    form.event({
                        event: "form_error",
                        form_id: "signup_form",
                        form_name: "Sign Up Form",
                        additional_parameters: {
                            message: $t({id: "REGISTER_ERROR_6"}),
                        },
                    });
                }
            } catch (er) {
                setError($t({id: "SomeError"}));
            } finally {
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        if (inviteCode || promoCode) {
            formik.setFieldValue("referral", inviteCode || promoCode);
        }
    }, [inviteCode, promoCode]);

    useEffect(() => {
        const keys = Object.keys(formik.errors);
        keys.forEach((item) => {
            form.error({
                field: item,
                message: formik.errors[item as keyof TFormikConfig] as string,
                form_id: "signup_form",
                form_name: "Sign Up Form",
            });
        });
    }, [formik.errors]);

    useEffect(() => {
        initForm(formik);
    }, []);

    useEffect(() => {
        const firstErrorKey = Object.keys(formik.errors)[0];

        if (firstErrorKey) {
            const input = document.querySelector(`[name="${firstErrorKey}"]`);

            // Находим обёртку поля по типу (input или checkbox)
            const wrapper =
                input?.closest('[class*="FormInput_box"]') ||
                input?.closest('[class*="CheckBox_box"]');

            if (wrapper) {
                setTimeout(() => {
                    wrapper.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }, 100);
            }
        }
    }, [formik.submitCount]);

    return (
        <Auth.Form.Wrapper>
            <form id="signup_form" autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                <Auth.Form.FormGroup noMobileScroll>
                    <Auth.Form.Row noMobile>
                        <Auth.Form.FormTitle>{$t({id: "CreateAnAccount"})}</Auth.Form.FormTitle>
                    </Auth.Form.Row>
                    <Auth.Form.Row gap>
                        <FormInput
                            placeholder={$t({id: "EnterUsername"})}
                            name="name"
                            type="text"
                            label={$t({id: "Username"})}
                            icon="Account"
                            required
                            size="sm"
                            value={formik.values?.name}
                            errors={
                                (!!formik.errors?.name && !!formik.touched?.name) || error
                                    ? {
                                          message: formik.errors?.name || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("name", val)}
                            onFocus={(e) => {
                                formik.setFieldTouched("name", false);
                                e.currentTarget.scrollIntoView({behavior: "smooth", block: "start"});
                            }}
                            onBlur={() => formik.setFieldTouched("name", true)}
                        />
                        <FormInput
                            placeholder={$t({id: "EnterEmail"})}
                            name="email"
                            type="email"
                            label={$t({id: "EmailAddress"})}
                            required
                            size="sm"
                            value={formik.values?.email}
                            errors={
                                (!!formik.errors?.email && !!("email" in formik.touched)) || error
                                    ? {
                                          message: formik.errors?.email || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("email", val)}
                            // onFocus={() => formik.setFieldTouched("email", false)}
                            onFocus={(e) => {
                               formik.setFieldTouched("email", false);
                               e.currentTarget.scrollIntoView({behavior: "smooth", block: "start"});
                            }}
                            onBlur={() => formik.setFieldTouched("email", true)}
                        />
                        <FormInput
                            placeholder={$t({id: "EnterPassword"})}
                            name="password"
                            type="password"
                            label={$t({id: "Password"})}
                            required
                            size="sm"
                            value={formik.values?.password}
                            errors={
                                (!!formik.errors?.password && !!("password" in formik.touched)) || error
                                    ? {
                                          message: formik.errors?.password || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("password", val)}
                            onFocus={(e) => {
                                formik.setFieldTouched("password", false);
                                e.currentTarget.scrollIntoView({behavior: "smooth", block: "start"});
                            }}
                            onBlur={() => formik.setFieldTouched("password", true)}
                        />
                        <Auth.Form.AddReferral onClick={() => setIsReferral(true)} text={$t({id: "AddReferralCode"})}>
                            <FormInput
                                placeholder={$t({id: "EnterReferralCode"})}
                                name="referral"
                                type="text"
                                label=""
                                required
                                size="sm"
                                disabled={!!inviteCode || !!promoCode}
                                className="referral-input"
                                value={formik.values?.referral}
                                errors={null}
                                onChange={(val) => formik.setFieldValue("referral", val)}
                                onFocus={(e) => {
                                    formik.setFieldTouched("referral", false);
                                    e.currentTarget.scrollIntoView({behavior: "smooth", block: "start"});
                                }}
                                onBlur={() => formik.setFieldTouched("referral", true)}
                            />
                        </Auth.Form.AddReferral>
                        <CheckBox
                            id={111}
                            name="confirm"
                            className="checkbox"
                            checked={formik.values?.confirm}
                            errors={
                                (!!formik.errors?.confirm && !!formik.touched?.confirm) || error
                                    ? {
                                          message: formik.errors?.confirm || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("confirm", val)}>
                            {$t(
                                {id: "IAgreeToAB"},
                                {
                                    A: (
                                        <Link
                                            to=""
                                            onClick={(e) => {
                                                e.preventDefault();
                                                dispatch(setLoginType(LoginType.HIDE));
                                                navigate(langNavigate("/help/TermsAndConditions"));
                                            }}>
                                            {$t({id: "TermsAndConditions"})}
                                        </Link>
                                    ),
                                    B: (
                                        <Link
                                            to=""
                                            onClick={(e) => {
                                                e.preventDefault();
                                                dispatch(setLoginType(LoginType.HIDE));
                                                navigate(langNavigate("/help/PrivacyPolicy"));
                                            }}>
                                            {$t({id: "PrivacyPolicy"})}
                                        </Link>
                                    ),
                                },
                            )}
                        </CheckBox>
                    </Auth.Form.Row>
                </Auth.Form.FormGroup>
                <Auth.Form.Row>
                    <Auth.Form.Button type="submit" text={$t({id: "SignUp"})} />
                </Auth.Form.Row>
            </form>
            <Auth.Form.Divider padding marginTop text={$t({id: "OrContinueWith"})} />
            <AuthLinks />
            <Auth.Form.NavLink
                text={$t({id: "AlreadyHaveHousebetsAccount"})}
                linkText={$t({id: "SignIn"})}
                onClick={() => {
                    onTabChange(0);
                }}
            />
        </Auth.Form.Wrapper>
    );
};

SignUp.displayName = "Auth.SignUp";
